import React, { useEffect, useState } from 'react';
import ModalShell from './ModalShell';
import { getBuckets, renameCategoryApi, updateCategoryBucketApi } from '../../lib/api';

/**
 * Phase 21c — Unified edit modal.
 *
 * Preset categories: name is read-only, only bucket editable.
 * Custom categories: both name and bucket editable.
 */
export default function RenameCategoryModal({ colors, category, onClose, onDone }) {
  const isPreset = !!category.is_preset;
  const [name, setName] = useState(category.name);
  const [bucket, setBucket] = useState(category.bucket || 'expense');
  const [buckets, setBuckets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { getBuckets().then(setBuckets).catch(() => setBuckets([])); }, []);

  const trimmedName = name.trim();
  const nameChanged = !isPreset && trimmedName && trimmedName !== category.name;
  const bucketChanged = bucket !== (category.bucket || 'expense');
  const canSubmit = (nameChanged || bucketChanged) && !!bucket && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true); setError(null);
    try {
      if (nameChanged) await renameCategoryApi(category.id, trimmedName);
      if (bucketChanged) await updateCategoryBucketApi(category.id, bucket);
      const bucketMeta = buckets.find((b) => b.code === bucket);
      onDone({
        name: nameChanged ? trimmedName : category.name,
        bucket,
        bucketLabel: bucketChanged ? (bucketMeta?.label || bucket) : null,
      });
    } catch (e) {
      setError(e?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const title = isPreset ? 'Edit account type' : 'Edit category';

  return (
    <ModalShell colors={colors} onClose={onClose} title={title} testId="mc-edit-modal">
      <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>
        Name{isPreset ? ' (locked)' : ''}
      </label>
      <input
        data-testid="mc-edit-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={40}
        disabled={isPreset}
        autoFocus={!isPreset}
        className="w-full px-3 py-2 rounded-lg text-sm outline-none"
        style={{
          backgroundColor: isPreset ? colors.bg : colors.bg,
          color: isPreset ? colors.textMuted : colors.text,
          border: `1px solid ${colors.border}`,
          opacity: isPreset ? 0.7 : 1,
          cursor: isPreset ? 'not-allowed' : 'text',
        }}
      />
      {isPreset ? (
        <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
          Preset names are locked for QuickBooks alignment.
        </p>
      ) : null}

      <label className="block text-[10px] font-bold uppercase tracking-wider mt-3 mb-1" style={{ color: colors.textMuted }}>
        Account type
      </label>
      <select
        data-testid="mc-edit-bucket"
        value={bucket}
        onChange={(e) => setBucket(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm outline-none"
        style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
      >
        {buckets.map((b) => (
          <option key={b.code} value={b.code}>{b.label}</option>
        ))}
      </select>
      <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
        Maps to QuickBooks chart of accounts.
      </p>

      {error ? <p className="text-xs mt-2" style={{ color: colors.error }}>{error}</p> : null}
      <div className="mt-3 flex gap-2">
        <button
          data-testid="mc-edit-cancel"
          onClick={onClose}
          className="flex-1 py-2 rounded-lg text-sm font-semibold border"
          style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textMuted }}
        >Cancel</button>
        <button
          data-testid="mc-edit-submit"
          onClick={submit}
          disabled={!canSubmit}
          className="flex-1 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ backgroundColor: colors.accent, opacity: canSubmit ? 1 : 0.5 }}
        >{saving ? '…' : 'Save'}</button>
      </div>
    </ModalShell>
  );
}
